import type {
  EggDefinition,
  EnemyDefinition,
  ItemDefinition,
  StatModifiers,
  UpgradeDefinition,
  WeaponDefinition,
} from "./types";

const ITEM_EFFECTS: Readonly<
  Record<string, Pick<ItemDefinition, "effect" | "limit">>
> = {
  "lucky-spoon": {
    effect: { type: "material-on-kill", chance: 0.12, amount: 1 },
    limit: 4,
  },
  "vacuum-lid": { effect: { type: "heal-on-pickup", amount: 1 }, limit: 3 },
  "energy-jam": { effect: { type: "damage-on-pickup", damage: 16 }, limit: 4 },
  "four-leaf-toast": {
    effect: { type: "free-rerolls", amount: 1 },
    limit: 3,
  },
  "slow-cooker": {
    effect: { type: "harvesting-growth", percent: 0.05 },
    limit: 3,
  },
  "tablecloth-net": { effect: { type: "tree-spawns", amount: 1 }, limit: 4 },
  "breakfast-combo": {
    effect: { type: "damage-on-enemy-death", damage: 12 },
    limit: 4,
  },
  "loaded-dice": {
    effect: { type: "material-on-kill", chance: 0.3, amount: 1 },
    limit: 2,
  },
  "eternal-kettle": { effect: { type: "weapon-upgrade-on-shop" }, limit: 1 },
  "miracle-butter": {
    effect: { type: "low-health-damage", percent: 35 },
    limit: 1,
  },
};

export const WEAPONS: readonly WeaponDefinition[] = [
  weapon("shell-pistol", "Shell Pistol", "Reliable single shots.", "precise", 14, 18, 27, 760, 8, 72, 1, 0, 0, "#ffcc4d", "round"),
  weapon("fork", "Dinner Fork", "Fast, short-ranged stabs.", "kitchen", 16, 13, 22, 560, 12, 24, 1, 0, 1, "#dfe7ef", "needle"),
  weapon("yolk-blaster", "Yolk Blaster", "A messy three-shot burst.", "rapid", 18, 9, 35, 640, 7, 66, 3, 0.2, 0, "#ffb703", "round"),
  weapon("whisk", "Battle Whisk", "Wide close-range sweep.", "kitchen", 20, 16, 42, 410, 20, 24, 3, 0.42, 0, "#f4f1de", "blade"),
  weapon("eggbeater", "Eggbeater", "A relentless stream of metal.", "rapid", 24, 7, 10, 720, 5, 54, 1, 0, 0, "#b8c7d9", "needle"),
  weapon("toast-launcher", "Toast Launcher", "Slow slices hit very hard.", "heavy", 28, 38, 58, 470, 18, 90, 1, 0, 2, "#c9833e", "blade"),
  weapon("pepper-shaker", "Pepper Shaker", "Pepper clouds cover a cone.", "rapid", 22, 6, 16, 520, 5, 38, 5, 0.5, 0, "#7b6b72", "round"),
  weapon("frying-pan", "Frying Pan", "Heavy close-range arcs.", "kitchen", 30, 44, 66, 370, 26, 22, 2, 0.26, 2, "#77828c", "blade"),
  weapon("butter-knife", "Butter Knife", "Quick blades punch through.", "precise", 18, 16, 25, 690, 10, 58, 1, 0, 2, "#f0e7d8", "blade"),
  weapon("hot-sauce", "Hot Sauce", "Sprays a fan of burning drops.", "elemental", 26, 11, 29, 510, 8, 46, 4, 0.38, 0, "#ef5d60", "orb"),
  weapon("freezer-pop", "Freezer Pop", "Cold shards pierce a crowd.", "elemental", 27, 23, 44, 650, 10, 76, 2, 0.12, 2, "#8be8f7", "needle"),
  weapon("rolling-pin", "Rolling Pin", "A colossal tumbling projectile.", "heavy", 32, 55, 78, 360, 30, 84, 1, 0, 4, "#ad8b73", "blade"),
  weapon("coffee-sprayer", "Coffee Sprayer", "Caffeinated rapid fire.", "rapid", 25, 8, 12, 790, 6, 58, 2, 0.09, 0, "#9c6644", "round"),
  weapon("soda-popper", "Soda Popper", "Bubbly shots flood the room.", "weird", 24, 10, 19, 590, 9, 76, 3, 0.28, 0, "#75c9c8", "orb"),
  weapon("magnet-coil", "Magnet Coil", "Dense bolts pass through targets.", "weird", 34, 29, 48, 820, 11, 88, 1, 0, 5, "#b392f0", "orb"),
  weapon("egg-timer", "Egg Timer", "A punctual radial volley.", "weird", 31, 18, 64, 600, 8, 72, 8, 6.283, 0, "#ffcf56", "round"),
  weapon("rubber-spatula", "Rubber Spatula", "Flexible close-range triples.", "kitchen", 23, 15, 31, 480, 15, 27, 3, 0.3, 1, "#ef476f", "blade"),
  weapon("salt-cannon", "Salt Cannon", "A brutal white scattershot.", "heavy", 36, 14, 70, 680, 9, 49, 8, 0.55, 0, "#fff8e7", "round"),
  weapon("blender-blade", "Blender Blade", "Ripping blades with deep pierce.", "kitchen", 38, 31, 52, 540, 18, 53, 2, 0.16, 6, "#aeb8c4", "blade"),
  weapon("toaster-arc", "Toaster Arc", "Electric orbs sweep the arena.", "elemental", 40, 34, 58, 500, 14, 100, 3, 0.32, 3, "#ffd166", "orb"),
  weapon("jam-jar", "Jam Jar", "Sticky, oversized berry globs.", "weird", 29, 42, 72, 330, 24, 100, 2, 0.2, 1, "#bb3e75", "orb"),
  weapon("milk-wand", "Milk Wand", "A clean line of piercing drops.", "elemental", 35, 22, 34, 920, 8, 90, 2, 0.08, 4, "#edf6f9", "needle"),
  weapon("breakfast-laser", "Breakfast Laser", "Needle-fast concentrated light.", "precise", 48, 66, 82, 1500, 6, 32, 1, 0, 20, "#ff4d6d", "needle"),
  weapon("golden-ladle", "Golden Ladle", "Royal volleys sweep everything.", "heavy", 52, 46, 70, 620, 17, 110, 6, 0.42, 3, "#ffd60a", "orb"),
] as const;

export const ITEMS: readonly ItemDefinition[] = [
  item("tiny-carton", "Tiny Carton", "+8 max shell.", 11, 1, { maxHealth: 8 }),
  item("running-shoes", "Running Shoes", "+18 movement speed.", 12, 1, { speed: 18 }),
  item("paper-plate", "Paper Plate", "+3 armor.", 13, 1, { armor: 3 }),
  item("hot-pepper", "Hot Pepper", "+8% damage.", 13, 1, { damagePercent: 8 }),
  item("alarm-clock", "Alarm Clock", "+9% attack speed.", 14, 1, { attackSpeedPercent: 9 }),
  item("long-straw", "Long Straw", "+80 pickup range.", 11, 1, { pickupRadius: 80 }),
  item("bouncy-napkin", "Bouncy Napkin", "+3% dodge.", 12, 1, { dodgePercent: 3 }),
  item("warm-mug", "Warm Mug", "+0.3 shell regeneration.", 14, 1, { regenerationPerSecond: 0.3 }),
  item("small-spring", "Small Spring", "+10% projectile speed.", 11, 1, { projectileSpeedPercent: 10 }),
  item("sturdy-cup", "Sturdy Cup", "+5 shell and +1 armor.", 15, 1, { maxHealth: 5, armor: 1 }),
  item("sugar-cube", "Sugar Cube", "+12 speed, +4% attack speed.", 15, 1, { speed: 12, attackSpeedPercent: 4 }),
  item("ketchup-packet", "Ketchup Packet", "+6% damage, +20 pickup range.", 14, 1, { damagePercent: 6, pickupRadius: 20 }),
  item("oven-mitt", "Oven Mitt", "+6 armor, -8 speed.", 21, 2, { armor: 6, speed: -8 }),
  item("protein-toast", "Protein Toast", "+15 max shell.", 22, 2, { maxHealth: 15 }),
  item("double-espresso", "Double Espresso", "+18% attack speed.", 24, 2, { attackSpeedPercent: 18 }),
  item("sharp-grater", "Sharp Grater", "+15% damage.", 25, 2, { damagePercent: 15 }),
  item("roller-skate", "Roller Skate", "+34 movement speed.", 23, 2, { speed: 34 }),
  item("vacuum-lid", "Vacuum Lid", "+180 pickup range.", 20, 2, { pickupRadius: 180 }),
  item("lucky-spoon", "Lucky Spoon", "+7% dodge.", 24, 2, { dodgePercent: 7 }),
  item("soup-thermos", "Soup Thermos", "+0.7 shell regeneration.", 26, 2, { regenerationPerSecond: 0.7 }),
  item("compressed-air", "Compressed Air", "+24% projectile speed.", 21, 2, { projectileSpeedPercent: 24 }),
  item("lunch-box", "Lunch Box", "+10 shell, +3 armor.", 25, 2, { maxHealth: 10, armor: 3 }),
  item("energy-jam", "Energy Jam", "+10% damage and attack speed.", 27, 2, { damagePercent: 10, attackSpeedPercent: 10 }),
  item("rubber-sole", "Rubber Sole", "+20 speed, +4% dodge.", 25, 2, { speed: 20, dodgePercent: 4 }),
  item("cast-iron-lid", "Cast-Iron Lid", "+12 armor, -18 speed.", 36, 3, { armor: 12, speed: -18 }),
  item("giant-carton", "Giant Carton", "+28 max shell.", 35, 3, { maxHealth: 28 }),
  item("triple-shot", "Triple Shot", "+30% attack speed, -5% damage.", 38, 3, { attackSpeedPercent: 30, damagePercent: -5 }),
  item("chef-knife", "Chef Knife", "+28% damage.", 40, 3, { damagePercent: 28 }),
  item("rocket-apron", "Rocket Apron", "+58 movement speed.", 37, 3, { speed: 58 }),
  item("tablecloth-net", "Tablecloth Net", "+320 pickup range.", 32, 3, { pickupRadius: 320 }),
  item("four-leaf-toast", "Four-Leaf Toast", "+12% dodge.", 39, 3, { dodgePercent: 12 }),
  item("slow-cooker", "Slow Cooker", "+1.4 shell regeneration.", 42, 3, { regenerationPerSecond: 1.4 }),
  item("pressure-nozzle", "Pressure Nozzle", "+42% projectile speed.", 34, 3, { projectileSpeedPercent: 42 }),
  item("fortified-bowl", "Fortified Bowl", "+18 shell, +8 armor.", 43, 3, { maxHealth: 18, armor: 8 }),
  item("breakfast-combo", "Breakfast Combo", "+18% damage and attack speed.", 45, 3, { damagePercent: 18, attackSpeedPercent: 18 }),
  item("greased-tray", "Greased Tray", "+42 speed, +7% dodge.", 42, 3, { speed: 42, dodgePercent: 7 }),
  item("titanium-shell", "Titanium Shell", "+45 shell and +15 armor.", 62, 4, { maxHealth: 45, armor: 15 }),
  item("midnight-coffee", "Midnight Coffee", "+48% attack speed.", 60, 4, { attackSpeedPercent: 48 }),
  item("dragon-sauce", "Dragon Sauce", "+45% damage.", 64, 4, { damagePercent: 45 }),
  item("jet-skates", "Jet Skates", "+90 movement speed.", 58, 4, { speed: 90 }),
  item("black-hole-cup", "Black-Hole Cup", "+600 pickup range.", 54, 4, { pickupRadius: 600 }),
  item("loaded-dice", "Loaded Dice", "+18% dodge.", 63, 4, { dodgePercent: 18 }),
  item("eternal-kettle", "Eternal Kettle", "+2.6 shell regeneration.", 66, 4, { regenerationPerSecond: 2.6 }),
  item("rail-nozzle", "Rail Nozzle", "+70% projectile speed.", 56, 4, { projectileSpeedPercent: 70 }),
  item("royal-platter", "Royal Platter", "+30 shell, +12 armor.", 65, 4, { maxHealth: 30, armor: 12 }),
  item("sunrise-special", "Sunrise Special", "+32% damage and attack speed.", 70, 4, { damagePercent: 32, attackSpeedPercent: 32 }),
  item("miracle-butter", "Miracle Butter", "+60 speed and +12% dodge.", 68, 4, { speed: 60, dodgePercent: 12 }),
  item("perfect-breakfast", "Perfect Breakfast", "A little bit of everything.", 80, 4, { maxHealth: 20, armor: 6, damagePercent: 18, attackSpeedPercent: 18, speed: 25, regenerationPerSecond: 0.8 }),
] as const;

export const UPGRADES: readonly UpgradeDefinition[] = [
  upgrade("thicker-shell", "Thicker Shell", "+10 max shell.", { maxHealth: 10 }),
  upgrade("quick-feet", "Quick Feet", "+24 movement speed.", { speed: 24 }),
  upgrade("hard-boiled", "Hard-Boiled", "+4 armor.", { armor: 4 }),
  upgrade("spicy-yolk", "Spicy Yolk", "+10% damage.", { damagePercent: 10 }),
  upgrade("rapid-crack", "Rapid Crack", "+12% attack speed.", { attackSpeedPercent: 12 }),
  upgrade("strong-throw", "Strong Throw", "+18% projectile speed.", { projectileSpeedPercent: 18 }),
  upgrade("wide-reach", "Wide Reach", "+120 pickup range.", { pickupRadius: 120 }),
  upgrade("lucky-roll", "Lucky Roll", "+4% dodge.", { dodgePercent: 4 }),
  upgrade("shell-mend", "Shell Mend", "+0.5 regeneration.", { regenerationPerSecond: 0.5 }),
  upgrade("battle-breakfast", "Battle Breakfast", "+6 shell and +5% damage.", { maxHealth: 6, damagePercent: 5 }),
  upgrade("morning-rush", "Morning Rush", "+12 speed and +7% attack speed.", { speed: 12, attackSpeedPercent: 7 }),
  upgrade("ceramic-coat", "Ceramic Coat", "+2 armor and +3% dodge.", { armor: 2, dodgePercent: 3 }),
  upgrade("sharp-prongs", "Sharp Prongs", "+3 melee damage.", { meleeDamage: 3 }),
  upgrade("steady-aim", "Steady Aim", "+3 ranged damage.", { rangedDamage: 3 }),
  upgrade("spiced-flame", "Spiced Flame", "+3 elemental damage.", { elementalDamage: 3 }),
  upgrade("critical-crack", "Critical Crack", "+5% critical chance.", { critChancePercent: 5 }),
  upgrade("siphon-straw", "Siphon Straw", "+3% life steal.", { lifeStealPercent: 3 }),
  upgrade("long-reach", "Long Reach", "+25 range.", { range: 25 }),
  upgrade("lucky-break", "Lucky Break", "+10 luck.", { luck: 10 }),
  upgrade("morning-harvest", "Morning Harvest", "+5 harvesting.", { harvesting: 5 }),
  upgrade("kitchen-engineer", "Kitchen Engineer", "+3 engineering.", { engineering: 3 }),
  upgrade("firm-grip", "Firm Grip", "+8 knockback.", { knockback: 8 }),
] as const;

export const EGGS: readonly EggDefinition[] = [
  egg("fresh-egg", "Fresh Egg", "Balanced", "Simple, dependable, and ready for anything.", "shell-pistol", {}, "#ffb703"),
  egg("hard-boiled", "Hard-Boiled", "Tank", "Thick shell, heavy pan, slower feet.", "frying-pan", { maxHealth: 22, armor: 9, speed: -34 }, "#7e8a97"),
  egg("runner-egg", "Runner Egg", "Speed", "Outruns trouble and sometimes slips through it.", "fork", { speed: 68, dodgePercent: 6, maxHealth: -10 }, "#47b8b7"),
  egg("scramble-egg", "Scramble Egg", "Rapid fire", "Attacks in a frenzy but each hit is lighter.", "eggbeater", { attackSpeedPercent: 34, damagePercent: -12 }, "#f4a261"),
  egg("sunny-egg", "Sunny Egg", "Glass cannon", "A bright center with explosive damage.", "yolk-blaster", { damagePercent: 32, maxHealth: -24 }, "#ffd60a"),
  egg("poached-egg", "Poached Egg", "Recovery", "Soft movements and constant shell repair.", "coffee-sprayer", { regenerationPerSecond: 1.2, speed: 18, armor: -3 }, "#bde0fe"),
  egg("double-yolk", "Double Yolk", "Multishot", "Every weapon adds one more projectile.", "soda-popper", { attackSpeedPercent: -14, damagePercent: -8 }, "#ff9f1c", 1),
  egg("magnet-egg", "Magnet Egg", "Collector", "Pulls materials from almost anywhere.", "magnet-coil", { pickupRadius: 440, projectileSpeedPercent: 12 }, "#b392f0"),
  egg("giant-egg", "Giant Egg", "Juggernaut", "Enormous shell and armor, tiny sense of urgency.", "rolling-pin", { maxHealth: 48, armor: 7, speed: -62 }, "#ad8b73"),
  egg("spicy-egg", "Spicy Egg", "Aggressor", "Fast hot volleys, brittle protection.", "hot-sauce", { damagePercent: 16, attackSpeedPercent: 16, armor: -6 }, "#ef5d60"),
  egg("lucky-egg", "Lucky Egg", "Trickster", "Dodges danger and peppers the arena.", "pepper-shaker", { dodgePercent: 14, maxHealth: -8 }, "#80ed99"),
  egg("royal-egg", "Royal Egg", "Arsenal", "Eight weapon slots, royal firepower, fragile pride.", "golden-ladle", { maxHealth: -30, damagePercent: 10, attackSpeedPercent: -10 }, "#ffd166", 0, 8),
] as const;

export const ENEMIES: readonly EnemyDefinition[] = [
  enemy("crumb", "Crumb", "normal", "drift", 1, 14, 12, 120, 4, 1, "#c9833e"),
  enemy("mite", "Jam Mite", "normal", "chase", 1, 23, 24, 94, 8, 1, "#a64d74"),
  enemy("scuttler", "Red Scuttler", "normal", "zigzag", 2, 18, 17, 138, 6, 1, "#ef5d60"),
  enemy("brute", "Purple Brute", "normal", "chase", 8, 35, 66, 62, 13, 3, "#76436f"),
  enemy("roller", "Dough Roller", "normal", "orbit", 4, 28, 48, 102, 10, 2, "#bb9457"),
  enemy("stinger", "Sugar Stinger", "normal", "charge", 3, 17, 32, 150, 9, 2, "#e9c46a"),
  enemy("slime", "Berry Slime", "normal", "drift", 14, 31, 82, 76, 12, 3, "#9b5de5"),
  enemy("moth", "Flour Moth", "normal", "orbit", 11, 20, 44, 130, 8, 2, "#d8e2dc"),
  enemy("charger", "Skillet Charger", "normal", "charge", 18, 29, 105, 88, 18, 4, "#6c757d"),
  enemy("shellback", "Shellback", "normal", "pulse", 13, 39, 170, 58, 20, 5, "#457b9d"),
  enemy("red-captain", "Red Captain", "elite", "zigzag", 5, 46, 420, 118, 22, 14, "#e63946"),
  enemy("void-cook", "Void Cook", "elite", "orbit", 9, 52, 620, 96, 27, 18, "#5a189a"),
  enemy("iron-maw", "Iron Maw", "elite", "charge", 14, 62, 900, 86, 34, 24, "#495057"),
  enemy("jam-queen", "Jam Queen", "boss", "pulse", 10, 90, 2100, 72, 30, 70, "#c9184a"),
  enemy("clockwork-chef", "Clockwork Chef", "boss", "charge", 20, 102, 3200, 64, 38, 100, "#6c757d"),
  enemy("the-breakfast", "The Breakfast", "boss", "orbit", 21, 110, 4200, 82, 42, 130, "#ff9f1c"),
  enemy("tree", "Breakfast Tree", "neutral", "stationary", 1, 34, 90, 0, 0, 3, "#4f772d"),
] as const;

export const WEAPON_BY_ID = new Map(WEAPONS.map((entry) => [entry.id, entry]));
export const ITEM_BY_ID = new Map(ITEMS.map((entry) => [entry.id, entry]));
export const UPGRADE_BY_ID = new Map(UPGRADES.map((entry) => [entry.id, entry]));
export const EGG_BY_ID = new Map(EGGS.map((entry) => [entry.id, entry]));
export const ENEMY_BY_ID = new Map(ENEMIES.map((entry) => [entry.id, entry]));

function weapon(
  id: string,
  name: string,
  description: string,
  family: WeaponDefinition["family"],
  basePrice: number,
  damage: number,
  intervalTicks: number,
  projectileSpeed: number,
  projectileRadius: number,
  projectileLifetimeTicks: number,
  projectileCount: number,
  spreadRadians: number,
  pierces: number,
  color: string,
  shape: WeaponDefinition["shape"],
): WeaponDefinition {
  return { id, name, description, family, behavior: weaponBehavior(id, family), basePrice, damage, intervalTicks, projectileSpeed, projectileRadius, projectileLifetimeTicks, projectileCount, spreadRadians, pierces, color, shape };
}

function item(
  id: string,
  name: string,
  description: string,
  basePrice: number,
  rarity: ItemDefinition["rarity"],
  modifiers: StatModifiers,
): ItemDefinition {
  return { id, name, description, basePrice, rarity, modifiers, ...ITEM_EFFECTS[id] };
}

function weaponBehavior(
  id: string,
  family: WeaponDefinition["family"],
): WeaponDefinition["behavior"] {
  if (["fork", "whisk", "frying-pan", "butter-knife", "rolling-pin", "rubber-spatula", "blender-blade"].includes(id)) return "melee";
  if (["toast-launcher", "salt-cannon", "jam-jar"].includes(id)) return "explosive";
  if (["soda-popper", "magnet-coil", "toaster-arc"].includes(id)) return "bouncing";
  if (id === "egg-timer") return "orbiting";
  if (id === "golden-ladle") return "structure";
  return family === "elemental" ? "explosive" : "projectile";
}

function upgrade(
  id: string,
  name: string,
  description: string,
  modifiers: StatModifiers,
): UpgradeDefinition {
  return { id, name, description, modifiers };
}

function egg(
  id: string,
  name: string,
  archetype: string,
  description: string,
  startingWeaponId: string,
  modifiers: StatModifiers,
  accent: string,
  bonusProjectiles?: number,
  weaponSlotLimit?: number,
): EggDefinition {
  return {
    id,
    name,
    archetype,
    description,
    startingWeaponId,
    modifiers,
    accent,
    ...(bonusProjectiles === undefined ? {} : { bonusProjectiles }),
    ...(weaponSlotLimit === undefined ? {} : { weaponSlotLimit }),
  };
}

function enemy(
  id: EnemyDefinition["id"],
  name: string,
  rank: EnemyDefinition["rank"],
  behavior: EnemyDefinition["behavior"],
  minimumWave: number,
  radius: number,
  health: number,
  speed: number,
  damage: number,
  materialValue: number,
  color: string,
): EnemyDefinition {
  return {
    id,
    name,
    rank,
    behavior,
    minimumWave,
    radius,
    health,
    speed,
    damage,
    materialValue,
    color,
  };
}
